/**
 * MC Resource History -- CPU/RAM/Disk history chart on a server's detail page (HEALTH-02),
 * extended by Phase 231 (HEALTH-03) with two more stacked panes -- TPS/MSPT from Lodestar's
 * heartbeat stream -- and extended again by Phase 235 (OPS-01) with four more stacked panes --
 * Heap/GC/Loaded Chunks/Live Entities from Lodestar's `heartbeat.ops` object.
 *
 * One Highcharts instance with stacked y-axis panes sharing a single x-axis, crosshair and
 * tooltip (D-06). CPU/RAM/Disk are ALWAYS present when this section renders; TPS/MSPT are
 * appended below Disk ONLY when the payload's `heartbeatCoverage.earliest !== null` (231 D-07/
 * D-09); Heap/GC/Chunks/Entities are appended below MSPT ONLY when the payload's
 * `opsCoverage.earliest !== null` (235 D-15) -- a genuinely SEPARATE gate from heartbeatCoverage,
 * never combined with it (235-RESEARCH.md Pitfall #1) -- see buildPanes() below, the D-06
 * extension point both later phases consume. Each pane renders BOTH the AVG line and the MIN-MAX
 * band behind it (D-03) over a D-04 24h/7d/30d/90d selectable range, fetched from
 * McPresenter::handleGetResourceChart (Phase 229 Plan 02, Phase 231 Plan 03) -- EXCEPT the three
 * band-less OPS panes (gc_collections/chunks/entities, `PaneDef.band === false`), which render a
 * plain single line only (235-UI-SPEC.md 7.5). CPU auto-scales host-relative (D-10); RAM/Disk/
 * Heap plot GB absolutes with a CURRENT limit/max drawn as a reference line (229 D-09, 235 D-12);
 * TPS/MSPT plot raw ticks/ms with the 231 D-08 ideal/tick-budget reference lines. The GC pane
 * (235 D-13) is a DELIBERATE exception to 231 D-07's "never overlay different unit systems on one
 * plot area" rule -- gc_collections (count) and gc_time (ms) share ONE pane via twin same-band
 * y-axes (see the `flattenAxes()` helper) because the two are causally linked and the "GC
 * pressure" story reads better together than split. A coverage subtitle states real history depth
 * while it is younger than the selected range, independently for ptero (D-05, Phase 229),
 * heartbeat (231 D-09) and ops (235 D-15) data.
 *
 * Gaps break the line and downtime segments are shaded behind every pane (D-08), and the
 * exported appendLatest() live-appends genuinely-new samples on the 24h raw range from
 * McPresenter::handleGetResourceTail -- never from the status-cache poll payload (D-07, Phase
 * 229 Plan 04). Bucketed ranges (7d/30d/90d) never receive a raw appended point. TPS/MSPT/OPS
 * panes do NOT live-append this phase (no heartbeat tail method ships for them) -- they refresh
 * on range change only; appendLatest() stays scoped to the original ptero series (BASE_PANES)
 * exactly as Ph229 shipped it.
 *
 * Registered from src/js/mc.ts's initDetail() -- see that file for the idempotent init call and
 * pollDetail()'s appendLatest() hook.
 */

import * as Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import { getChartTheme } from '../utils/chartTheme';
import { chartInstantHeader } from '../utils/instants';

// v12: highcharts-more self-registers on side-effect import (HighchartsMore factory removed).
// 'arearange' (D-03's min/max band) is the canonical highcharts-more series type -- used here
// as the module-registration sentinel (loud guard, mirrors src/js/stardew.ts's D-04 pattern).
if (typeof (Highcharts as any).seriesTypes?.['arearange'] === 'undefined') {
    console.error('[highcharts-more] module failed to register (arearange missing) -- resource-history bands will not render. Check the highcharts-more side-effect import.');
}

// ---------------------------------------------------------------------------
// Types -- mirrors McPresenter::handleGetResourceChart's JSON contract exactly
// (229-02-SUMMARY.md "JSON Contract -- Final Shape").
// ---------------------------------------------------------------------------

// Phase 231 D-07: widened to include the two heartbeat-sourced metrics -- 'tps'/'mspt' join
// 'cpu'/'memory'/'disk' as ordinary pane metrics, consumed by the exact same buildYAxis()/
// buildSeries() loops as the original three (no metric-specific branching beyond unit/plotLine).
// Phase 235 (OPS-01): widened again with the five heartbeat.ops-sourced metrics -- 'heap' (arearange
// band + plotLine), 'gc_collections'/'gc_time' (D-13's combined dual-axis pane), 'chunks',
// 'entities' (plain lines, no band).
type MetricKey =
    | 'cpu'
    | 'memory'
    | 'disk'
    | 'tps'
    | 'mspt'
    | 'heap'
    | 'gc_collections'
    | 'gc_time'
    | 'chunks'
    | 'entities';

interface ResourceSeriesPoint {
    t: string;                 // 'Y-m-d H:i:s', UTC (matches the DB) -- NOT epoch-ms.
    avg: number | null;
    min: number | null;
    max: number | null;
}

interface ResourceCoverage {
    earliest: string | null;   // null only on a genuinely empty (never-collected) server.
    daysCovered: number;
    daysRequested: number;
    complete: boolean;
}

interface ResourceDowntimeWindow {
    start: string;
    // Rule 1 -- type correction: getUptimeTimeline()'s existing segment shape leaves `end` null
    // for a still-open/ongoing downtime window (detail.latte's own uptime timeline already
    // branches on this: `{if $segment['end']}...{else}now{/if}`). This field was typed as a
    // non-nullable string before this plan ever consumed it; a currently-down server would have
    // silently broken the plotBand math below.
    end: string | null;
}

interface ResourceChartPayload {
    range: string;
    aggregation: string;
    series: {
        cpu: ResourceSeriesPoint[];
        memory: ResourceSeriesPoint[];
        disk: ResourceSeriesPoint[];
        // Phase 231 D-07 -- source='heartbeat', McAnalyticsModel::getResourceChartData()
        // (231-03). Independent of cpu/memory/disk -- never cross-filtered with source='ptero'.
        tps: ResourceSeriesPoint[];
        mspt: ResourceSeriesPoint[];
        // Phase 235 (OPS-01) -- source='heartbeat', from the widened heartbeat.ops object
        // (plan 235-04). heap_max_bytes is deliberately NOT a series here -- it rides the
        // top-level `heapMaxBytes` single-latest-value field below (235-RESEARCH.md Pitfall #4).
        heap: ResourceSeriesPoint[];
        gc_collections: ResourceSeriesPoint[];
        gc_time: ResourceSeriesPoint[];
        chunks: ResourceSeriesPoint[];
        entities: ResourceSeriesPoint[];
    };
    coverage: ResourceCoverage;
    // Phase 231 D-09 -- independently computed from `coverage` (which stays ptero-scoped and
    // unaffected by heartbeat presence). `earliest === null` is the "never applies" (mod-less)
    // signal that hides the TPS/MSPT panes entirely; see buildPanes() below.
    heartbeatCoverage: ResourceCoverage;
    diskLimitBytes: number | null;
    // Phase 235 (OPS-01) -- a THIRD, genuinely SEPARATE coverage signal from heartbeatCoverage
    // above (same four-key shape, reused as-is -- plan 235-04 deliberately returns the identical
    // shape so no new TS type is needed). A server with old heartbeat-only history has non-null
    // heartbeatCoverage.earliest but null opsCoverage.earliest -- see buildPanes() below.
    opsCoverage: ResourceCoverage;
    // Phase 235 (OPS-01) -- the CURRENT (not historical) heap max, mirroring diskLimitBytes'
    // shape but resolved from the latest sampled row, not a static config column (Pattern 2).
    heapMaxBytes: number | null;
    downtime: ResourceDowntimeWindow[];
}

// ---------------------------------------------------------------------------
// D-07 -- the getResourceTail(key, since) tail-feed contract (229-02-SUMMARY.md). Always raw
// source='ptero' sample rows -- the appended point must NEVER come from the status-cache poll
// payload (mc.ts's DetailServerData), which is what makes an offline tick correctly append
// nothing with zero client-side special-casing.
// ---------------------------------------------------------------------------

interface ResourceTailRow {
    t: string;
    cpu: number | null;
    memory: number | null;
    disk: number | null;
}

interface ResourceTailPayload {
    rows: ResourceTailRow[];
}

// ---------------------------------------------------------------------------
// Pane definition -- the D-06 Phase-231 extension point.
//
// Phase 231's TPS/MSPT overlay lands on THIS SAME chart (its success criterion #4 requires it),
// by pushing one more entry onto this array. Pane top/height below are computed FROM this list's
// length + weights (computePaneLayout()), never hardcoded per-pane, so appending a pane here is
// the only change 231 needs to make to the layout -- it never has to renegotiate positioning.
// ---------------------------------------------------------------------------

// Generic over the metric, defaulting to the full union -- BASE_PANES below pins it to just
// 'cpu'|'memory'|'disk' so appendRowsToChart()'s `row[pane.metric]` (row: ResourceTailRow, which
// has no tps/mspt fields) stays type-checked without a cast; buildPanes()'s computed array uses
// the default (full MetricKey) since it may also carry 'tps'/'mspt' entries.
interface PaneDef<M extends MetricKey = MetricKey> {
    metric: M;
    title: string;
    weight: number;    // relative vertical share of the stacked chart
    unit: 'percent' | 'bytes' | 'tps' | 'ms' | 'count';
    // Phase 235 (D-13) -- a second metric sharing THIS pane's single layout slot on its own
    // opposite-side y-axis. Used only by the GC pane (gc_collections is the primary metric,
    // gc_time rides here) -- see flattenAxes() below, the one genuinely new mechanism this phase
    // needs (235-PATTERNS.md "No Analog Found"). Absent on every other pane.
    secondary?: { metric: MetricKey; title: string };
    // Phase 235 -- defaults to true (arearange band + line, unchanged pre-235 behaviour) when
    // absent. Set false to render a single plain line series with NO min/max band -- used by
    // gc_collections/chunks/entities per 235-UI-SPEC.md 7.5's pane-type column.
    band?: boolean;
}

// CPU/RAM/Disk -- ALWAYS present whenever this chart section renders at all, regardless of
// heartbeat presence (D-09: "CPU/RAM/Disk render exactly as Ph229 ships them"). This is also
// the exact pane set appendLatest()/appendRowsToChart() live-append against (see below) -- their
// series-index math (`i * 2`/`i * 2 + 1`) depends on this array's order/length staying the FIRST
// N series in the chart regardless of whether tps/mspt panes are concatenated after it.
const BASE_PANES: PaneDef<'cpu' | 'memory' | 'disk'>[] = [
    { metric: 'cpu', title: 'CPU (% of host, may exceed 100)', weight: 2, unit: 'percent' },
    { metric: 'memory', title: 'RAM (GB)', weight: 2, unit: 'bytes' },
    // D-06: Disk barely moves tick to tick -- rendered shorter than CPU/RAM.
    { metric: 'disk', title: 'Disk (GB)', weight: 1, unit: 'bytes' },
];

/**
 * Phase 231 D-09 -- the computed pane list for the CURRENT payload. Starts from BASE_PANES
 * (always included) and conditionally concatenates the TPS/MSPT panes below Disk ONLY when
 * `heartbeatCoverage.earliest !== null` -- a genuinely empty (never-collected/mod-less) server
 * gets no TPS/MSPT panes at all, degrading to Ph229's exact three-pane chart. `earliest !== null`
 * (some heartbeat history, possibly young) shows both panes; the "collecting since" honesty is
 * carried by the coverage subtitle (renderHeartbeatCoverageSubtitle()), not by omitting the pane.
 *
 * computePaneLayout()/buildYAxis()/buildSeries() all iterate whatever array THIS function
 * returns -- no further layout change is needed to add/remove panes (229 D-06's whole point).
 */
function buildPanes(payload: ResourceChartPayload): PaneDef[] {
    const panes: PaneDef[] = [...BASE_PANES];
    if (payload.heartbeatCoverage && payload.heartbeatCoverage.earliest !== null) {
        panes.push(
            { metric: 'tps', title: 'TPS (ticks/sec)', weight: 1, unit: 'tps' },
            { metric: 'mspt', title: 'MSPT (ms/tick)', weight: 1, unit: 'ms' },
        );
    }

    // Phase 235 (OPS-01) -- a THIRD conditional block, genuinely SEPARATE from the
    // heartbeatCoverage gate above. A server heartbeating since before this phase has a
    // non-null heartbeatCoverage.earliest and ZERO ops rows -- reusing that signal here would
    // render all 4 new panes immediately with a misleadingly early "collecting since" date
    // (235-RESEARCH.md Pitfall #1). NEVER OR/AND this condition with heartbeatCoverage above,
    // and never reference heartbeatCoverage inside this block.
    if (payload.opsCoverage && payload.opsCoverage.earliest !== null) {
        panes.push(
            // "Server Ops -- " prefix goes on THIS pane's title ONLY (D-15's labeled section
            // header, resolved by 235-UI-SPEC.md 7.5 as a plain axis-title-text prefix -- no new
            // rendering primitive, no resize handling needed).
            { metric: 'heap', title: 'Server Ops — Heap (GB)', weight: 2, unit: 'bytes' },
            // D-13 -- ⚠ a DELIBERATE, INFORMED departure from 231 D-07's own "never overlay
            // different unit systems on one plot area" rule. gc_collections (a count) and
            // gc_time (milliseconds) are causally linked -- more collections drives more GC
            // time -- and seeing both together tells the "GC pressure" story in one glance; this
            // was weighed against 231 D-07's rule during discussion and chosen anyway
            // (235-CONTEXT.md D-13). Do NOT "fix" this back into two panes. gc_time rides this
            // pane's `secondary` slot so it shares ONE layout slot with gc_collections (see
            // flattenAxes() below), not its own PaneDef -- this pushes 4 PaneDefs carrying 5
            // metrics, not 5 PaneDefs.
            {
                metric: 'gc_collections',
                title: 'GC Collections (count)',
                weight: 1,
                unit: 'count',
                band: false,
                secondary: { metric: 'gc_time', title: 'GC Time (ms)' },
            },
            { metric: 'chunks', title: 'Loaded Chunks (count)', weight: 1, unit: 'count', band: false },
            { metric: 'entities', title: 'Live Entities (count)', weight: 1, unit: 'count', band: false },
        );
    }

    return panes;
}

const PANE_COLORS: Record<MetricKey, string> = {
    cpu: '#2196F3',
    memory: '#4CAF50',
    disk: '#FF9800',
    // Phase 231 D-07 -- distinct from the D-08 reference-line colors (green/red) so the data
    // line and its own threshold line never render in the same hue on the same pane.
    tps: '#9C27B0',
    mspt: '#00BCD4',
    // Phase 235 (OPS-01) -- extends the Material-hue data-viz palette with 4 more distinct hues,
    // never reusing an existing hue family (235-UI-SPEC.md 7.5). Hardcoded hex, same convention
    // as the five above -- never accent-tinted, never routed through a --ksat-* token.
    heap: '#3F51B5',
    gc_collections: '#FFC107',
    gc_time: '#E64A19',
    chunks: '#795548',
    entities: '#F06292',
};

const PANE_GAP_PCT = 3;
const BYTES_PER_GB = 1024 ** 3;

// D-07: the raw 24h range's own window width. Live-appended points that age past this fall off
// the front of the series so a long-open tab's chart stays bounded instead of growing forever.
const WINDOW_24H_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let chartInstance: Highcharts.Chart | null = null;

// D-07 live-append state -- tracks what the CURRENTLY RENDERED chart actually shows, so
// appendLatest() (called from mc.ts's existing poll cycle) knows the active server/range and
// where to resume tail-fetching from. Set exclusively inside fetchAndRender(), the one place a
// full payload is ever rendered (initial load or a range-button click).
let activeServerKey: string | null = null;
let activeRange = '24h';
let lastPlottedAt: string | null = null; // most recently plotted point's DB 't' string, or null

// WR-01-style idempotency guard: prevents double-binding the range-button click listeners if
// initResourceHistory() is ever invoked twice on the same container (feedback_shared_component_
// every_page_init_double_bind) -- chartInstance's own destroy-before-create guard (below) already
// covers the Highcharts-instance half of that risk.
const initializedContainers = new WeakSet<HTMLElement>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initResourceHistory(container: HTMLElement): void {
    const chartEl = container.querySelector<HTMLElement>('#mc-resource-history-chart');
    if (!chartEl) return; // Section not present on this page render.

    const serverKey = container.dataset.serverKey;
    if (!serverKey) return;

    if (initializedContainers.has(container)) return;
    initializedContainers.add(container);

    const rangeButtons = Array.from(container.querySelectorAll<HTMLElement>('.mc-range-btn'));
    let currentRange = getActiveRange(rangeButtons);

    rangeButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
            const range = btn.dataset.range;
            if (!range || range === currentRange) return;

            currentRange = range;

            rangeButtons.forEach((b) => {
                b.classList.remove('btn-primary');
                b.classList.add('btn-outline-secondary');
            });
            btn.classList.remove('btn-outline-secondary');
            btn.classList.add('btn-primary');

            fetchAndRender(container, chartEl, serverKey, currentRange);
        });
    });

    fetchAndRender(container, chartEl, serverKey, currentRange);
}

function getActiveRange(rangeButtons: HTMLElement[]): string {
    const active = rangeButtons.find((b) => b.classList.contains('btn-primary'));
    return active?.dataset.range ?? '24h';
}

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

async function fetchAndRender(
    container: HTMLElement,
    chartEl: HTMLElement,
    serverKey: string,
    range: string,
): Promise<void> {
    const url = `?do=getResourceChart&key=${encodeURIComponent(serverKey)}&range=${encodeURIComponent(range)}`;

    try {
        const resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error(`[mc-resource-history] chart fetch failed: ${resp.status}`);
            return;
        }

        const payload: ResourceChartPayload = await resp.json();

        // D-07: this IS the one place a full payload is ever rendered -- record what the chart
        // now actually shows so appendLatest() (poll-driven, mc.ts) knows the current range/key
        // and can resume tail-fetching from the right point.
        activeServerKey = serverKey;
        activeRange = range;
        lastPlottedAt = computeLastPlottedAt(payload);

        renderCoverageSubtitle(container, payload.coverage);
        renderHeartbeatCoverageSubtitle(container, payload.heartbeatCoverage);
        renderOpsCoverageSubtitle(container, payload.opsCoverage);
        renderChart(chartEl, payload);
    } catch (err) {
        console.error('[mc-resource-history] chart fetch error:', err);
    }
}

function computeLastPlottedAt(payload: ResourceChartPayload): string | null {
    let last: string | null = null;
    (['cpu', 'memory', 'disk'] as MetricKey[]).forEach((key) => {
        const points = payload.series[key];
        if (points.length === 0) return;
        const t = points[points.length - 1].t;
        // 'Y-m-d H:i:s' is lexicographically sortable -- a plain string comparison is correct.
        if (last === null || t > last) last = t;
    });
    return last;
}

// ---------------------------------------------------------------------------
// Coverage subtitle (D-05)
// ---------------------------------------------------------------------------

function renderCoverageSubtitle(container: HTMLElement, coverage: ResourceCoverage): void {
    const el = container.querySelector<HTMLElement>('.mc-resource-coverage');
    if (!el) return;

    // T-229-08: textContent ONLY -- coverage is server-composed from numeric counts + a date,
    // but it is still server data reaching the DOM, so it is never assigned via innerHTML.
    if (coverage.earliest === null) {
        // 229-02-SUMMARY.md's decision: earliest !== null is the "collector is alive" signal,
        // not daysCovered. A null earliest is the genuine day-one/never-collected case.
        el.textContent = 'Collection has just begun -- check back soon for history.';
        return;
    }

    if (coverage.complete) {
        el.textContent = '';
        return;
    }

    const earliestLabel = formatCoverageDate(coverage.earliest);
    const dayWord = coverage.daysCovered === 1 ? 'day' : 'days';
    el.textContent = `Collecting since ${earliestLabel} · ${coverage.daysCovered} ${dayWord} of ${coverage.daysRequested}`;
}

function formatCoverageDate(dbDatetime: string): string {
    const ts = parseUtcTimestamp(dbDatetime);
    if (isNaN(ts)) return dbDatetime;
    // v1.17 TZ display strategy: Intl.DateTimeFormat (platform-native, no per-call timeZone
    // override) -- matches every other date-label formatter in this codebase.
    return new Intl.DateTimeFormat('en-CA', { day: 'numeric', month: 'short' }).format(new Date(ts));
}

// ---------------------------------------------------------------------------
// Heartbeat coverage subtitle (Phase 231 D-09)
//
// detail.latte does NOT pre-render a heartbeat-scoped subtitle element (this plan's scope is
// this module only) -- so unlike renderCoverageSubtitle() above (which fills an ALREADY-PRESENT
// `.mc-resource-coverage` <p>), this lazily creates its own element on first use, placed right
// after the existing ptero subtitle. The class name is deliberately NOT `.mc-resource-coverage`
// (T-231's own scoping requirement) so a `container.querySelector('.mc-resource-coverage')`
// elsewhere always resolves to the ptero-only element, never this one.
// ---------------------------------------------------------------------------

function renderHeartbeatCoverageSubtitle(container: HTMLElement, coverage: ResourceCoverage): void {
    let el = container.querySelector<HTMLElement>('.mc-resource-coverage-heartbeat');

    if (!coverage || coverage.earliest === null) {
        // D-09: genuinely zero heartbeat rows ever (mod-less) -- no TPS/MSPT panes, so no
        // subtitle either. Remove a stale element from a prior range/server that DID have one.
        if (el) el.remove();
        return;
    }

    if (!el) {
        el = document.createElement('p');
        el.className = 'mc-resource-coverage-heartbeat text-muted small mb-2';
        const pteroSubtitle = container.querySelector('.mc-resource-coverage');
        if (pteroSubtitle) {
            pteroSubtitle.after(el);
        } else {
            container.querySelector('#mc-resource-history-chart')?.before(el);
        }
    }

    // T-231-08: textContent ONLY -- never innerHTML -- mirrors renderCoverageSubtitle()'s own
    // T-229-08 discipline exactly; this is still server-composed data reaching the DOM.
    if (coverage.complete) {
        el.textContent = '';
        return;
    }

    const earliestLabel = formatCoverageDate(coverage.earliest);
    const dayWord = coverage.daysCovered === 1 ? 'day' : 'days';
    el.textContent = `TPS/MSPT collecting since ${earliestLabel} · ${coverage.daysCovered} ${dayWord} of ${coverage.daysRequested}`;
}

// ---------------------------------------------------------------------------
// Server Ops coverage subtitle (Phase 235 D-15)
//
// Structural copy of renderHeartbeatCoverageSubtitle() above, changed only: the element class
// (`.mc-resource-coverage-ops`, T-231's own scoping requirement extended one more time), the
// insertion point (after the heartbeat subtitle when one exists, falling back to the same
// ptero-subtitle-then-chart-element cascade that function uses), and the locked copy string
// (235-UI-SPEC.md Copywriting Contract: "Server Ops collecting since..."). Every other lifecycle
// branch (remove-when-inapplicable, blank-when-complete, formatCoverageDate()/day-pluralisation
// reuse) is identical.
// ---------------------------------------------------------------------------

function renderOpsCoverageSubtitle(container: HTMLElement, coverage: ResourceCoverage): void {
    let el = container.querySelector<HTMLElement>('.mc-resource-coverage-ops');

    if (!coverage || coverage.earliest === null) {
        // D-15: genuinely zero ops rows ever -- no Heap/GC/Chunks/Entities panes, so no subtitle
        // either. Remove a stale element from a prior range/server that DID have one.
        if (el) el.remove();
        return;
    }

    if (!el) {
        el = document.createElement('p');
        el.className = 'mc-resource-coverage-ops text-muted small mb-2';
        const heartbeatSubtitle = container.querySelector('.mc-resource-coverage-heartbeat');
        const pteroSubtitle = container.querySelector('.mc-resource-coverage');
        if (heartbeatSubtitle) {
            heartbeatSubtitle.after(el);
        } else if (pteroSubtitle) {
            pteroSubtitle.after(el);
        } else {
            container.querySelector('#mc-resource-history-chart')?.before(el);
        }
    }

    // textContent ONLY -- never innerHTML -- mirrors renderHeartbeatCoverageSubtitle()'s own
    // T-231-08/T-229-08 discipline exactly; this is still server-composed data reaching the DOM.
    if (coverage.complete) {
        el.textContent = '';
        return;
    }

    const earliestLabel = formatCoverageDate(coverage.earliest);
    const dayWord = coverage.daysCovered === 1 ? 'day' : 'days';
    el.textContent = `Server Ops collecting since ${earliestLabel} · ${coverage.daysCovered} ${dayWord} of ${coverage.daysRequested}`;
}

// ---------------------------------------------------------------------------
// Chart rendering
// ---------------------------------------------------------------------------

/**
 * Phase 240 (240-05, D-07/SC5): hand-replicates Highcharts' own built-in default shared-tooltip
 * row rendering -- colored bullet + series name + value -- for a single point. This chart had NO
 * `tooltip.formatter` before this change; it relied entirely on that default. Mirrors
 * `makeEmptyAwareTooltip()`'s shared-mode row-building loop SHAPE (colored bullet, series name,
 * bold value) -- deliberately NOT its filtering/sorting behavior, which would drop legitimate
 * zero-valued OPS rows (`gc_collections` on an idle server is legitimately 0) and reorder the
 * panes, exactly the SC5 regression this helper must avoid.
 *
 * Values are already in DISPLAY units by the time they reach here -- `buildSeries()` calls
 * `scaleValue()` (bytes -> GB) before Highcharts ever sees a number, and no series sets
 * `valueSuffix` -- so `Highcharts.numberFormat(value, -1)` (Highcharts' own automatic-decimals
 * behavior, matching what the default renderer already produces) with NO added unit suffix is
 * correct; the unit already lives in the series name (`RAM (GB)`, `MSPT (ms/tick)`).
 */
function formatResourceTooltipRow(p: Highcharts.Point): string {
    // T-240-17: color must be a real CSS color string, never a gradient/pattern object that
    // could serialize unexpectedly into the `style` attribute (same guard as emptyAwareTooltip.ts).
    const color = typeof p.series.color === 'string' ? p.series.color : '#aaa';
    // T-240-16: p.series.name is NEVER wire-controlled -- every value comes from `PaneDef.title`,
    // a hardcoded literal in BASE_PANES/buildPanes(), optionally suffixed with the literal
    // ' range'. No user/player/Lodestar-supplied string reaches this interpolation.
    const name = p.series.name;

    // The arearange band series (D-03's min/max band, pushed first for every pane where
    // `pane.band !== false`) renders `low - high`, matching Highcharts' own arearange default
    // pointFormat. Detected by series TYPE, never by parsing the ' range' name suffix -- band
    // panes are the common case here (most panes have one), not an edge case.
    // T-240-18: every field access below is nullish-guarded -- a formatter that throws breaks
    // every tooltip on the page, so no unguarded property chain is acceptable.
    if (p.series.type === 'arearange') {
        const rangePoint = p as unknown as { low?: number | null; high?: number | null };
        const valueHtml = (rangePoint.low == null || rangePoint.high == null)
            ? '<b></b>'
            : `<b>${Highcharts.numberFormat(rangePoint.low, -1)}</b> - <b>${Highcharts.numberFormat(rangePoint.high, -1)}</b>`;
        return `<span style="color:${color}">●</span> ${name}: ${valueHtml}`;
    }

    // Default (line) series row. A null point (p.y == null) renders with an empty bold value
    // rather than being skipped -- matching what the default shared-tooltip renderer does for a
    // null point, and never silently dropping the row.
    const valueHtml = p.y == null ? '<b></b>' : `<b>${Highcharts.numberFormat(p.y, -1)}</b>`;
    return `<span style="color:${color}">●</span> ${name}: ${valueHtml}`;
}

function renderChart(chartEl: HTMLElement, payload: ResourceChartPayload): void {
    if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
    }

    // Phase 181: getChartTheme() supplies chrome only (background/axis/tooltip surface) and
    // registers axis chrome as a Highcharts global default, which is what actually reaches each
    // element of the yAxis ARRAY below (chartTheme.ts:62-68) -- Highcharts.merge() would otherwise
    // let the array wholesale-replace getChartTheme()'s single yAxis chrome object.
    // No data-bs-theme read here -- getChartTheme() is the sole sanctioned reader (STATE.md).
    chartInstance = Highcharts.chart(chartEl, Highcharts.merge(getChartTheme(), {
        chart: {
            backgroundColor: 'transparent',
        },
        title: {
            text: '',
        },
        xAxis: {
            type: 'datetime',
            crosshair: true,
            // D-08: downtime shading. The payload's `downtime` is already computed for the
            // SELECTED range server-side (Plan 02), so this is recomputed fresh on every
            // range-change re-render -- no hardcoded 7-day window here. This is the chart's only
            // xAxis (just yAxis is an array), so a plotBand here spans the full plot area behind
            // all three stacked panes.
            plotBands: buildDowntimePlotBands(payload.downtime),
        },
        yAxis: buildYAxis(payload),
        // Phase 240 (240-05, D-05/D-07/D-08/SC2/SC5): leads with chartInstantHeader() -- the
        // exact browser-timezone instant plus its relative age -- above per-series rows that
        // hand-replicate Highcharts' own default shared-tooltip row rendering (formatResourceTooltipRow()
        // above), so Phase 235's up-to-nine stacked OPS panes render identically to today, just
        // with a new header line. The old built-in date-format tooltip option is dropped -- a
        // custom `formatter` supersedes it, and leaving a dead option in place would invite a
        // future reader to think it still drives the header.
        //
        // D-08 provenance (do not convert `this.x`): the payload field is `p.t`, a naive-UTC
        // 'Y-m-d H:i:s' string from `mc_server_resource_samples.sampled_at`; `parseUtcTimestamp()`
        // (below, ~line 1000) explicitly appends 'Z' before `Date.parse()`, so the single
        // conversion to a correct epoch already happened once, client-side, at that one function,
        // BEFORE any point ever reaches Highcharts. By the time a point reaches this formatter,
        // `this.x` is already a correct epoch-ms number -- pass it straight to
        // chartInstantHeader(). Do NOT re-parse it as a string (there is no string at this
        // layer), do NOT apply any manual offset "to be safe", and do NOT add a per-chart
        // browser-vs-UTC rendering-mode override -- that flag is GLOBAL (src/index.ts:56-61),
        // load-bearing, and a standing repo convention with its own project-memory entry.
        tooltip: {
            shared: true,
            formatter: function (this: Highcharts.Point): string {
                const header = chartInstantHeader(this.x as number);
                // SC5: iterate this.points in the order Highcharts hands them over -- do NOT
                // filter and do NOT sort (unlike makeEmptyAwareTooltip()'s shared branch, which
                // filters y<=0/null and sorts descending -- copying that loop wholesale here
                // would silently drop every zero-valued OPS row and reorder the panes).
                const rows = (this.points ?? []).map(formatResourceTooltipRow).join('<br/>');
                return header + '<br/>' + rows;
            } as Highcharts.TooltipFormatterCallbackFunction,
        },
        legend: {
            enabled: false,
        },
        series: buildSeries(payload),
        credits: { enabled: false },
    }));
}

function computePaneLayout(panes: PaneDef[]): { top: string; height: string }[] {
    const totalGap = PANE_GAP_PCT * (panes.length - 1);
    const totalWeight = panes.reduce((sum, p) => sum + p.weight, 0);
    const availableHeight = 100 - totalGap;

    let top = 0;
    const layout: { top: string; height: string }[] = [];
    for (const pane of panes) {
        const height = (availableHeight * pane.weight) / totalWeight;
        layout.push({ top: `${top}%`, height: `${height}%` });
        top += height + PANE_GAP_PCT;
    }
    return layout;
}

/**
 * Phase 235 (D-13) -- replaces buildYAxis()'s old 1:1 `panes.map((pane, i) => ...)` index
 * identity with an explicit flattening step, WITHOUT changing computePaneLayout()'s contract (it
 * still receives the pane list and still returns exactly one `{top, height}` per pane -- the GC
 * pane occupying one slot with two axes is entirely a buildYAxis()/buildSeries()-level concern).
 *
 * For a normal pane this emits ONE axis entry, unchanged from before. For a pane carrying
 * `secondary` (only the GC pane, D-13), it emits TWO entries sharing the SAME `top`/`height` from
 * that pane's single layout slot: the primary as always, and the secondary with `opposite: true`
 * (so its ticks render on the right edge instead of colliding with the left axis) and
 * `gridLineWidth: 0` (so the shared band does not get doubled gridlines).
 *
 * Both buildYAxis() and buildSeries() call this SAME helper (buildSeries() discards the `axes`
 * half and keeps only `axisIndexByMetric`) so the two can never drift apart on which index a
 * given metric's series should bind to.
 *
 * Non-regression property (D-15): because the 4 new OPS panes are appended AFTER all 5 existing
 * panes (buildPanes()), and the GC pane's extra axis entry is emitted immediately after its own
 * primary, the flattened indices 0-4 (cpu/memory/disk/tps/mspt) are EXACTLY what they were before
 * this phase -- this is what guarantees "without disturbing the existing panes".
 */
function flattenAxes(
    panes: PaneDef[],
    layout: { top: string; height: string }[],
): { axes: Highcharts.YAxisOptions[]; axisIndexByMetric: Partial<Record<MetricKey, number>> } {
    const axes: Highcharts.YAxisOptions[] = [];
    const axisIndexByMetric: Partial<Record<MetricKey, number>> = {};

    panes.forEach((pane, i) => {
        const { top, height } = layout[i];

        axisIndexByMetric[pane.metric] = axes.length;
        axes.push({
            top,
            height,
            offset: 0,
            min: 0,
            title: { text: pane.title },
        });

        if (pane.secondary) {
            axisIndexByMetric[pane.secondary.metric] = axes.length;
            axes.push({
                top,
                height,
                offset: 0,
                min: 0,
                opposite: true,
                gridLineWidth: 0,
                title: { text: pane.secondary.title },
            });
        }
    });

    return { axes, axisIndexByMetric };
}

function buildYAxis(payload: ResourceChartPayload): Highcharts.YAxisOptions[] {
    const panes = buildPanes(payload);
    const layout = computePaneLayout(panes);
    const { axes, axisIndexByMetric } = flattenAxes(panes, layout);

    panes.forEach((pane) => {
        const axisIndex = axisIndexByMetric[pane.metric];
        if (axisIndex === undefined) return;
        const axis = axes[axisIndex];

        // D-10: CPU is deliberately left with NO cap -- a multi-core allocation legitimately
        // reads well above 100%; capping the axis would clip real, correct data.

        // D-09 (Phase 229): the disk reference line is the CURRENT limit only, never a
        // historical value, and is labelled as such. No line is drawn when the limit is
        // unset/0 (unlimited).
        if (pane.metric === 'disk' && payload.diskLimitBytes) {
            const limitGb = bytesToGb(payload.diskLimitBytes);
            axis.plotLines = [{
                value: limitGb,
                color: '#dc3545',
                width: 1,
                dashStyle: 'Dash',
                zIndex: 5,
                label: {
                    text: `current disk limit (${limitGb.toFixed(1)} GB)`,
                    align: 'right',
                    style: { fontSize: '10px' },
                },
            }];
        }

        // Phase 231 D-08 -- the two reference lines, mirroring the disk-limit shape above:
        // TPS 20 is the ideal/ceiling tick rate; MSPT 50ms is the one-tick (20 TPS) time budget.
        // Both give an instant "is this healthy?" read without the viewer needing to know the
        // thresholds themselves.
        if (pane.metric === 'tps') {
            axis.plotLines = [{
                value: 20,
                color: '#28a745',
                width: 1,
                dashStyle: 'Dash',
                zIndex: 5,
                label: {
                    text: 'ideal (20 TPS)',
                    align: 'right',
                    style: { fontSize: '10px' },
                },
            }];
        }
        if (pane.metric === 'mspt') {
            axis.plotLines = [{
                value: 50,
                color: '#dc3545',
                width: 1,
                dashStyle: 'Dash',
                zIndex: 5,
                label: {
                    text: 'tick budget (50ms)',
                    align: 'right',
                    style: { fontSize: '10px' },
                },
            }];
        }

        // Phase 235 (D-12) -- the heap reference line, mirroring the disk-limit block above
        // exactly. The word "current" is load-bearing: a JVM restart with a different -Xmx moves
        // this value, so the line is a snapshot, not a historical constant -- the same honesty
        // caveat 229 D-09 applied to the disk limit. No line is drawn when heapMaxBytes is
        // unknown (null/0).
        if (pane.metric === 'heap' && payload.heapMaxBytes) {
            const maxGb = bytesToGb(payload.heapMaxBytes);
            axis.plotLines = [{
                value: maxGb,
                color: '#dc3545',
                width: 1,
                dashStyle: 'Dash',
                zIndex: 5,
                label: {
                    text: `current heap max (${maxGb.toFixed(1)} GB)`,
                    align: 'right',
                    style: { fontSize: '10px' },
                },
            }];
        }
        // No reference lines on gc_collections/gc_time/chunks/entities -- the wire contract
        // defines no "ideal" threshold for any of them, unlike TPS's 20/MSPT's 50; inventing one
        // would be a fabricated claim.
    });

    return axes;
}

function buildSeries(payload: ResourceChartPayload): Highcharts.SeriesOptionsType[] {
    const series: Highcharts.SeriesOptionsType[] = [];

    // D-08: break the line across a gap wider than ~2 poll/bucket cycles instead of Highcharts'
    // default of gliding smoothly across missing points. Expressed in axis (millisecond) units
    // via gapUnit: 'value' -- NOT gapUnit: 'relative' (the Highcharts default), which multiplies
    // by the series' own auto-detected pointInterval and is unreliable against a fetched,
    // occasionally-irregular series.
    const gapSizeMs = computeGapSizeMs(payload.aggregation);

    // Phase 231 D-07/D-09/Phase 235 D-15: the computed pane list for THIS payload -- tps/mspt/
    // heap/gc_collections/chunks/entities join cpu/memory/disk here only when their own coverage
    // signal applies; every metric flows through this SAME loop (no special-casing beyond
    // PANE_COLORS/scaleValue's unit branch and the band/secondary flags below).
    const panes = buildPanes(payload);
    const layout = computePaneLayout(panes);
    // Phase 235 (D-13): built via the SAME flattenAxes() helper buildYAxis() uses, so the
    // yAxis index a series binds to here can never drift apart from the axis buildYAxis()
    // actually rendered at that index. Only axisIndexByMetric is needed here -- the `axes` half
    // is discarded.
    const { axisIndexByMetric } = flattenAxes(panes, layout);

    panes.forEach((pane) => {
        const points = payload.series[pane.metric] ?? [];
        const color = PANE_COLORS[pane.metric];
        const yAxis = axisIndexByMetric[pane.metric] ?? 0;

        const rangeData: [number, number, number][] = [];
        const avgData: [number, number][] = [];

        for (const p of points) {
            const x = parseUtcTimestamp(p.t);
            if (isNaN(x)) continue;

            if (p.min !== null && p.max !== null) {
                rangeData.push([x, scaleValue(pane, p.min), scaleValue(pane, p.max)]);
            }
            if (p.avg !== null) {
                avgData.push([x, scaleValue(pane, p.avg)]);
            }
        }

        // D-03: BOTH the min/max band AND the avg line are mandatory for a banded pane --
        // rendering only one misses the decision. Band pushed first so it renders BEHIND the avg
        // line (Highcharts draws later series above earlier ones within the same pane).
        //
        // Phase 235 (UI-SPEC 7.5): `pane.band === false` (gc_collections/chunks/entities) skips
        // the arearange entirely -- those panes render a single plain line, no min/max band.
        if (pane.band !== false) {
            series.push({
                type: 'arearange',
                name: `${pane.title} range`,
                data: rangeData,
                yAxis,
                color,
                fillOpacity: 0.2,
                lineWidth: 0,
                marker: { enabled: false },
                zIndex: 0,
                gapSize: gapSizeMs,
                gapUnit: 'value',
            } as Highcharts.SeriesArearangeOptions);
        }

        series.push({
            type: 'line',
            name: pane.title,
            data: avgData,
            yAxis,
            color,
            lineWidth: 1.5,
            marker: { enabled: false },
            zIndex: 1,
            gapSize: gapSizeMs,
            gapUnit: 'value',
        } as Highcharts.SeriesLineOptions);

        // Phase 235 (D-13): the GC pane's secondary metric (gc_time) pushes its OWN plain line
        // series, bound to its OWN axis index -- this is what puts gc_collections against the
        // left axis and gc_time against the right one inside a single visual band. Same gap
        // handling and null-point discipline as every other series (a bucket with no ops rows
        // reads as a gap, never a zero).
        if (pane.secondary) {
            const secondaryMetric = pane.secondary.metric;
            const secondaryPoints = payload.series[secondaryMetric] ?? [];
            const secondaryColor = PANE_COLORS[secondaryMetric];
            const secondaryYAxis = axisIndexByMetric[secondaryMetric] ?? yAxis;
            const secondaryAvgData: [number, number][] = [];

            for (const p of secondaryPoints) {
                const x = parseUtcTimestamp(p.t);
                if (isNaN(x)) continue;
                if (p.avg !== null) {
                    secondaryAvgData.push([x, p.avg]);
                }
            }

            series.push({
                type: 'line',
                name: pane.secondary.title,
                data: secondaryAvgData,
                yAxis: secondaryYAxis,
                color: secondaryColor,
                lineWidth: 1.5,
                marker: { enabled: false },
                zIndex: 1,
                gapSize: gapSizeMs,
                gapUnit: 'value',
            } as Highcharts.SeriesLineOptions);
        }
    });

    return series;
}

// D-08: two-poll-cycle gap threshold, per range's own cadence -- so a single missing bucket at
// 30d/90d does not spuriously break the line, while 24h's 5-minute Ptero cadence (matches
// cron/mc/snapshot.php) still breaks promptly on a real outage.
function computeGapSizeMs(aggregation: string): number {
    const cadenceMinutes: Record<string, number> = {
        raw: 5,       // 24h: 5-minute Ptero poll cadence
        '15min': 15,  // 7d bucket width
        hourly: 60,   // 30d bucket width
        '3h': 180,    // 90d bucket width
    };
    const minutes = cadenceMinutes[aggregation] ?? 5;
    return 2 * minutes * 60 * 1000;
}

// D-08: render the payload's range-consistent downtime segments as x-axis plotBands, upgrading
// a gap's meaning from "no data" to "server was down". Low-alpha, theme-consistent -- reuses the
// same danger red already used for the D-09 disk-limit reference line rather than reading
// data-bs-theme directly (getChartTheme() is this project's sole sanctioned reader of that
// attribute).
function buildDowntimePlotBands(downtime: ResourceDowntimeWindow[]): Highcharts.XAxisPlotBandsOptions[] {
    const nowMs = Date.now();
    return downtime.map((seg): Highcharts.XAxisPlotBandsOptions => {
        const from = parseUtcTimestamp(seg.start);
        const to = seg.end !== null ? parseUtcTimestamp(seg.end) : nowMs;
        return {
            from: isNaN(from) ? nowMs : from,
            to: isNaN(to) ? nowMs : to,
            color: 'rgba(220, 53, 69, 0.12)',
            zIndex: 2,
        };
    });
}

// ---------------------------------------------------------------------------
// Live append (D-07) -- invoked from mc.ts's existing detail poll cycle (pollDetail()), NEVER
// from initResourceHistory()/renderChart(). Strictly additive: series.addPoint() only -- no
// chart re-initialization here. Idempotent -- re-invoking with no genuinely new rows is a no-op.
// ---------------------------------------------------------------------------

/**
 * Tail-fetch rows newer than the last plotted point and append them to the live chart.
 *
 * Applies to the raw 24h range ONLY -- appending a raw sample onto a bucketed (15min/hourly/3h)
 * series would mix granularities. Bucketed ranges simply skip live-update on the poll tick
 * (Claude's Discretion per 229-04-PLAN.md) rather than re-fetching their whole range every 30s.
 *
 * The appended point's source is ALWAYS handleGetResourceTail (real source='ptero' sample rows)
 * -- never the status-cache poll payload mc.ts's pollDetail() also fetches. Under D-01 an
 * offline tick writes no sample row, so an offline tick's tail-fetch correctly returns zero rows
 * with no client-side special-casing.
 */
export async function appendLatest(): Promise<void> {
    if (activeRange !== '24h') return;
    if (activeServerKey === null || chartInstance === null) return;

    // Young/day-one dataset: the initial 24h render may have had zero points, leaving
    // lastPlottedAt null. Anchor the first tail-fetch at the window's own start rather than
    // never calling the signal at all, so live-append still activates once real rows land.
    const since = lastPlottedAt ?? formatUtcDbString(Date.now() - WINDOW_24H_MS);
    const url = `?do=getResourceTail&key=${encodeURIComponent(activeServerKey)}&since=${encodeURIComponent(since)}`;

    try {
        const resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error(`[mc-resource-history] tail fetch failed: ${resp.status}`);
            return;
        }

        const payload: ResourceTailPayload = await resp.json();
        if (!payload.rows || payload.rows.length === 0) return; // idempotent no-op

        appendRowsToChart(payload.rows);
    } catch (err) {
        console.error('[mc-resource-history] tail fetch error:', err);
    }
}

function appendRowsToChart(rows: ResourceTailRow[]): void {
    if (chartInstance === null) return;
    const chart = chartInstance; // capture for narrowing inside the closures below

    let appendedAny = false;

    for (const row of rows) {
        const x = parseUtcTimestamp(row.t);
        if (isNaN(x)) continue;

        // Phase 231: appendLatest()/appendRowsToChart() stay scoped to BASE_PANES (cpu/memory/
        // disk) ONLY -- TPS/MSPT do not live-append this phase (231-03 ships no heartbeat tail
        // method; see the module docblock). This is also why BASE_PANES must stay the FIRST N
        // panes in buildPanes()'s output: BASE_PANES[i]'s index here (0/1/2) maps correctly into
        // chart.series regardless of whether tps/mspt panes were concatenated after it.
        BASE_PANES.forEach((pane, i) => {
            const value = row[pane.metric];
            if (value === null || value === undefined) return;

            // Series order mirrors buildSeries(): arearange pushed first, then line, per pane.
            const rangeSeries = chart.series[i * 2];
            const lineSeries = chart.series[i * 2 + 1];
            if (!rangeSeries || !lineSeries) return;

            // Idempotent: only append points strictly newer than what this series already
            // holds -- re-invoking appendLatest() with no genuinely new rows (or a row this
            // series has already plotted) is a no-op, never a duplicate point.
            const existing = lineSeries.data;
            const lastX = existing.length > 0 ? (existing[existing.length - 1].x as number) : -Infinity;
            if (x <= lastX) return;

            const scaled = scaleValue(pane, value);
            rangeSeries.addPoint([x, scaled, scaled], false, false);
            lineSeries.addPoint([x, scaled], false, false);
            appendedAny = true;
        });

        if (lastPlottedAt === null || row.t > lastPlottedAt) {
            lastPlottedAt = row.t;
        }
    }

    if (!appendedAny) return;

    // Trim points that have aged out of the 24h raw window so a long-open tab's chart stays
    // bounded instead of growing forever.
    const cutoff = Date.now() - WINDOW_24H_MS;
    chart.series.forEach((series) => {
        while (series.data.length > 0 && (series.data[0].x as number) < cutoff) {
            series.data[0].remove(false);
        }
    });

    chart.redraw();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Phase 231 D-07: 'tps'/'ms' fall through unchanged alongside the pre-existing 'percent'
// passthrough -- TPS/MSPT are raw values (ticks/sec, milliseconds), never byte-scaled.
function scaleValue(pane: PaneDef, value: number): number {
    return pane.unit === 'bytes' ? bytesToGb(value) : value;
}

function bytesToGb(bytes: number): number {
    return bytes / BYTES_PER_GB;
}

/**
 * DB datetime strings ('Y-m-d H:i:s') are UTC instants, never local. A bare Date.parse() on the
 * space-separated form is engine-dependent (V8 reads it as LOCAL time), which would silently
 * shift every point by the browser's UTC offset. Force UTC parsing by reformatting to a
 * Z-suffixed ISO string; the global browser-local rendering flag (src/index.ts) then renders/
 * labels the resulting epoch using the browser's local timezone -- the project's standing "UTC in DB, Toronto for
 * display" rule.
 */
function parseUtcTimestamp(t: string): number {
    return Date.parse(t.replace(' ', 'T') + 'Z');
}

/**
 * The inverse of parseUtcTimestamp() -- formats an epoch-ms instant back into the DB's own
 * 'Y-m-d H:i:s' UTC string shape, using UTC getters (not local getters) so the digits match what
 * McPresenter::handleGetResourceTail's `new \DateTime($since)` will compare against the naive-UTC
 * `sampled_at` column. Used only for appendLatest()'s young-dataset fallback `since` value.
 */
function formatUtcDbString(ms: number): string {
    const d = new Date(ms);
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} `
        + `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}
